import { useEffect, useCallback } from 'react'
import { publicDepositAccountService, PublicDepositAccount } from '../services/depositAccountService'

interface DepositAccountUpdateEvent {
    type: string
    action: string
    account: PublicDepositAccount
    newStatus?: string
    timestamp: string
}

interface UseDepositAccountUpdatesOptions {
    tenantId: string
    onAccountsUpdate?: (accounts: PublicDepositAccount[]) => void
    onDefaultAccountChange?: (account: PublicDepositAccount) => void
    onAccountStatusChange?: (account: PublicDepositAccount, newStatus: string) => void
    enabled?: boolean
}

export const useDepositAccountUpdates = ({
    tenantId,
    onAccountsUpdate,
    onDefaultAccountChange,
    onAccountStatusChange,
    enabled = true
}: UseDepositAccountUpdatesOptions) => {

    const handleDepositAccountEvent = useCallback(async (event: CustomEvent<DepositAccountUpdateEvent>) => {
        const { type, action, account, newStatus, timestamp } = event.detail

        console.log('🏦 Deposit account WebSocket event received:', {
            type,
            action,
            accountId: account._id,
            accountName: account.accountName,
            newStatus,
            timestamp
        })

        try {
            // Refresh deposit accounts from API to get latest data
            const updatedAccounts = await publicDepositAccountService.getActiveDepositAccounts(tenantId)

            // Call appropriate callback based on action
            switch (action) {
                case 'create':
                case 'update':
                case 'delete':
                    if (onAccountsUpdate) {
                        console.log('🔄 Calling onAccountsUpdate with refreshed data')
                        onAccountsUpdate(updatedAccounts)
                    }
                    break

                case 'set_default':
                    if (onDefaultAccountChange) {
                        console.log('⭐ Calling onDefaultAccountChange for new default account')
                        onDefaultAccountChange(account)
                    }
                    if (onAccountsUpdate) {
                        onAccountsUpdate(updatedAccounts)
                    }
                    break

                case 'toggle_status':
                    if (onAccountStatusChange && newStatus) {
                        console.log(`🔄 Calling onAccountStatusChange for ${newStatus} status`)
                        onAccountStatusChange(account, newStatus)
                    }
                    if (onAccountsUpdate) {
                        onAccountsUpdate(updatedAccounts)
                    }
                    break

                default:
                    // For any other action, just refresh accounts
                    if (onAccountsUpdate) {
                        onAccountsUpdate(updatedAccounts)
                    }
                    break
            }
        } catch (error) {
            console.error('❌ Error handling deposit account update:', error)
        }
    }, [tenantId, onAccountsUpdate, onDefaultAccountChange, onAccountStatusChange])

    useEffect(() => {
        if (!enabled || !tenantId) return

        // Add event listener for deposit account WebSocket events
        const eventListener = (event: Event) => {
            const customEvent = event as CustomEvent<DepositAccountUpdateEvent>
            handleDepositAccountEvent(customEvent)
        }

        window.addEventListener('deposit_account_websocket_event', eventListener)

        return () => {
            window.removeEventListener('deposit_account_websocket_event', eventListener)
        }
    }, [handleDepositAccountEvent, enabled, tenantId])

    // Return utility functions
    return {
        refreshAccounts: useCallback(async () => {
            try {
                const accounts = await publicDepositAccountService.getActiveDepositAccounts(tenantId)
                if (onAccountsUpdate) {
                    onAccountsUpdate(accounts)
                }
                return accounts
            } catch (error) {
                console.error('❌ Error refreshing deposit accounts:', error)
                return []
            }
        }, [tenantId, onAccountsUpdate])
    }
}

export default useDepositAccountUpdates
